import { styled, Tabs, Tab, TabProps, Chip, Typography } from "@mui/material";
import Box from "@mui/material/Box";

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

interface StyledTabsProps {
  children?: React.ReactNode;
  value: string;
  onChange: (event: React.SyntheticEvent, newValue: string) => void;
}

export const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))(({ theme }) => ({
  backgroundColor: "#ffffff",
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    width: "100%",
    backgroundColor: theme.palette.primary.main,
  },
  "& .MuiTabs-flexContainer": {
    height: "48px",
  },
}));

export const StyledTab = styled((props: TabProps) => <Tab {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    color: theme.palette.text.secondary,
    fontSize: "14px",
    fontWeight: 500,
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  })
);

export const MainTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2.5 }}>{children}</Box>}
    </div>
  );
};

export const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export const CustomTabPanel2 = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      style={{ height: "100%" }}
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ height: "100%" }}>{children}</Box>}
    </div>
  );
};

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

export const ModalTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
};

// V2 Modern Tab Components
interface StyledTabsV2Props {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

export const StyledTabsV2 = styled((props: StyledTabsV2Props) => (
  <Tabs
    {...props}
    variant="scrollable"
    scrollButtons="auto"
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))(({ theme }) => ({
  "& .MuiTabs-indicator": {
    backgroundColor: "primary.main",
    height: "3px",
    borderRadius: "2px",
  },
  "& .MuiTabs-indicatorSpan": {
    width: "100%",
    backgroundColor: "primary.main",
  },
  "& .MuiTabs-flexContainer": {
    height: "48px",
  },
}));

interface StyledTabV2Props extends Omit<TabProps, "icon"> {
  icon?: React.ReactNode;
  count?: number;
  countColor?: string;
  countBgColor?: string;
}

export const StyledTabV2 = styled((props: StyledTabV2Props) => {
  const { icon, count, countColor, countBgColor, label, ...other } = props;

  return (
    <Tab
      {...other}
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {icon}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {count !== undefined && (
            <Chip
              label={count}
              size="small"
              sx={{
                backgroundColor: countBgColor || "rgba(29, 78, 216, 0.1)",
                color: countColor || "#1d4ed8",
                fontSize: "11px",
                height: "20px",
                minWidth: "20px",
              }}
            />
          )}
        </Box>
      }
    />
  );
})(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "14px",
  minHeight: "48px",
  color: "#64748b",
  "&.Mui-selected": {
    color: theme.palette.primary.main || "#1d4ed8",
  },
  "&:hover": {
    color: theme.palette.primary.main || "#1d4ed8",
    backgroundColor: theme.palette.secondary.main || "rgba(29, 78, 216, 0.05)",
  },
}));

export const TabContainerV2 = styled(Box)(({ theme }) => ({
  borderBottom: "1px solid rgba(226, 232, 240, 0.6)",
  marginBottom: "24px",
}));

export const TabPanelV2 = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};
